import type { ReadonlyRecord } from "@core/foundation/types";
import type { ZodNever, ZodType } from "@core/zod";

import type { AuthType } from "../auth/index.ts";

import type { HttpMethod } from "./http-method.ts";

export interface ApiEndpoint<
  Body extends ZodType | undefined,
  QueryParams extends ZodType | undefined,
  Response extends ZodType<ReadonlyRecord<string, unknown> | void>,
  Streaming extends true | undefined,
> {
  readonly body: Body;
  readonly method: HttpMethod;
  readonly queryParams: QueryParams;
  readonly response: Response;
  readonly streaming: Streaming;
}

export type AnyApiEndpoint = ApiEndpoint<
  ZodType | undefined,
  ZodType | undefined,
  ZodType<ReadonlyRecord<string, unknown> | void>,
  true | undefined
>;

export type ApiEndpoints<Endpoints> = {
  [K in keyof Endpoints]: Endpoints[K] extends AnyApiEndpoint
    ? Endpoints[K]
    : never;
};

type AnyApiEndpoints = ReadonlyRecord<string, AnyApiEndpoint>;

export interface Api<
  AuthTypeParam extends AuthType,
  Domain extends string,
  Endpoints,
> {
  readonly authType: AuthTypeParam;
  readonly domain: Domain;
  readonly endpoints: ApiEndpoints<Endpoints>;
  readonly path: string;
  readonly scopes?: readonly string[];
}

export type AnyApi = Api<AuthType, string, AnyApiEndpoints>;

export function adminApi<Domain extends string, Endpoints>(
  { domain, path }: { readonly domain: Domain; readonly path: string },
  endpoints: ApiEndpoints<Endpoints>,
): Api<"admin", Domain, Endpoints> {
  return { authType: "admin", domain, endpoints, path };
}

export function api<Domain extends string, Endpoints>(
  {
    domain,
    path,
    scopes,
  }: {
    readonly domain: Domain;
    readonly path: string;
    readonly scopes?: readonly string[];
  },
  endpoints: ApiEndpoints<Endpoints>,
): Api<"user", Domain, Endpoints> {
  return { authType: "user", domain, endpoints, path, scopes };
}

interface ApiEndpointOptions<
  Body extends ZodType | undefined,
  QueryParams extends ZodType | undefined,
  Response extends ZodType<ReadonlyRecord<string, unknown> | void>,
  Streaming extends true | undefined,
> {
  readonly body?: Body;
  readonly queryParams?: QueryParams;
  readonly response: Response;
  readonly streaming?: Streaming;
}

export function apiEndpoint<
  Body extends ZodType | undefined = undefined,
  QueryParams extends ZodType | undefined = undefined,
  Response extends ZodType<ReadonlyRecord<string, unknown> | void> = ZodNever,
  Streaming extends true | undefined = undefined,
>(
  options: ApiEndpointOptions<Body, QueryParams, Response, Streaming>,
): ApiEndpoint<Body, QueryParams, Response, Streaming>;
export function apiEndpoint<
  Body extends ZodType | undefined = undefined,
  QueryParams extends ZodType | undefined = undefined,
  Response extends ZodType<ReadonlyRecord<string, unknown> | void> = ZodNever,
  Streaming extends true | undefined = undefined,
>(
  method: HttpMethod,
  options: ApiEndpointOptions<Body, QueryParams, Response, Streaming>,
): ApiEndpoint<Body, QueryParams, Response, Streaming>;
export function apiEndpoint<
  Body extends ZodType | undefined = undefined,
  QueryParams extends ZodType | undefined = undefined,
  Response extends ZodType<ReadonlyRecord<string, unknown> | void> = ZodNever,
  Streaming extends true | undefined = undefined,
>(
  ...args:
    | readonly [ApiEndpointOptions<Body, QueryParams, Response, Streaming>]
    | readonly [
        HttpMethod,
        ApiEndpointOptions<Body, QueryParams, Response, Streaming>,
      ]
): ApiEndpoint<Body, QueryParams, Response, Streaming> {
  const defaultMethod: HttpMethod = "POST";
  const [method, { body, queryParams, response, streaming }] =
    typeof args[0] === "string" ? [args[0], args[1]] : [defaultMethod, args[0]];

  const result: AnyApiEndpoint = {
    body,
    method,
    queryParams,
    response,
    streaming,
  };

  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  return result as ApiEndpoint<Body, QueryParams, Response, Streaming>;
}

export function internalApi<Domain extends string, Endpoints>(
  { domain, path }: { readonly domain: Domain; readonly path: string },
  endpoints: ApiEndpoints<Endpoints>,
): Api<"service", Domain, Endpoints> {
  return { authType: "service", domain, endpoints, path };
}

export function keyApi<Domain extends string, Endpoints>(
  { domain, path }: { readonly domain: Domain; readonly path: string },
  endpoints: ApiEndpoints<Endpoints>,
): Api<"key", Domain, Endpoints> {
  return { authType: "key", domain, endpoints, path };
}

export function publicApi<Domain extends string, Endpoints>(
  { domain, path }: { readonly domain: Domain; readonly path: string },
  endpoints: ApiEndpoints<Endpoints>,
): Api<"public", Domain, Endpoints> {
  return { authType: "public", domain, endpoints, path };
}
